import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

export type SkillScope = "system" | "user";

export interface Skill {
  id: string;
  ref: string;
  scope: SkillScope;
  name: string;
  description: string;
  label?: string;
  /** Scope of the row. Present in cross-space (`scope=all`) responses
   *  and on single-row GET / POST / PUT; `null` means the skill is in
   *  the caller's personal scope. Absent for system skills. */
  space?: { id: string; name: string } | null;
  /** Raw scope id, returned by single-row GET / POST / PUT. `null` =
   *  legacy personal (space_id IS NULL). Absent for system skills. */
  space_id?: string | null;
}

export interface SkillDetail extends Skill {
  content: string;
}

export interface SkillFormBody {
  name: string;
  label: string;
  description: string;
  content: string;
  /** Target tenancy. `null` = personal scope; `"<uuid>"` = pinned to
   *  that team space. Sent on every save — backend distinguishes from
   *  "field omitted" by `model_fields_set`, but the new UI is always
   *  explicit. */
  space_id?: string | null;
}

type Scope = "personal" | "space" | "all";

export const skillsQueryOptions = (scope: Scope = "all") =>
  queryOptions({
    queryKey: ["skills", scope],
    queryFn: async (): Promise<Skill[]> => {
      const resp = await apiFetch(
        `/api/skills?scope=${scope}`,
        undefined,
        scope === "all" ? { crossSpace: true } : undefined,
      );
      if (!resp.ok) throw new Error(await resp.text());
      const data: { skills: Skill[] } = await resp.json();
      return data.skills ?? [];
    },
    staleTime: 30_000,
  });

export const skillDetailQueryOptions = (ref: string) =>
  queryOptions({
    queryKey: ["skills", ref],
    queryFn: async (): Promise<SkillDetail> => {
      // Cross-space: catalog is cross-space, so detail must be too —
      // otherwise X-Space-Id mismatches the skill's home space and 404s.
      const resp = await apiFetch(`/api/skills/${ref}`, undefined, {
        crossSpace: true,
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    staleTime: 30_000,
  });

export async function createSkill(body: SkillFormBody): Promise<SkillDetail> {
  const resp = await apiFetch("/api/skills", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function updateSkill(ref: string, body: SkillFormBody): Promise<SkillDetail> {
  const resp = await apiFetch(`/api/skills/${ref}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function copySkill(
  ref: string,
  toSpaceId: string | null,
): Promise<SkillDetail> {
  const resp = await apiFetch(
    `/api/skills/${ref}/copy`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to_space_id: toSpaceId }),
    },
    // Catalog is cross-space, so the source ref may live in a different
    // space than the active sidebar — match the detail/list semantics.
    { crossSpace: true },
  );
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Ошибка копирования");
  }
  return resp.json();
}

export async function deleteSkill(ref: string): Promise<void> {
  const resp = await apiFetch(`/api/skills/${ref}`, { method: "DELETE" });
  if (!resp.ok) throw new Error(await resp.text());
}

export function skillLabel(skill: Pick<Skill, "name" | "label">): string {
  return skill.label || skill.name;
}
